// utils/guestTracking.js
//
// Fire-and-forget guest analytics for the guest-facing pages (checkin + guestapp).
// Sends behavioural events (open, tab, step, chatbot, completed, session_end)
// and errors to POST /guest-events. NEVER throws — tracking must never break
// the guest UX.
//
// Server-side already logs the "open" event (reliable, with IP/geo). This util
// adds the granular client events + session duration via navigator.sendBeacon.

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:4000';
const ENDPOINT = `${BASE_URL}/guest-events`;

// Module-level context so the axios interceptor / ErrorBoundary can attribute
// an error to the current guest page without prop-drilling.
let currentContext = { reservationId: null, surface: null };

// Guards against React StrictMode double-mount + repeated init.
const initialized = new Set();
// Listener cleanup handles per reservation.
const listeners = {};

export const getGuestContext = () => currentContext;

/** Stable per-visit session id (sessionStorage → survives reloads in the tab). */
const getSessionId = (reservationId) => {
  if (typeof window === 'undefined') return null;
  const key = `guest_session_${reservationId}`;
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
    sessionStorage.setItem(key, id);
  }
  return id;
};

/** Low-level send. Uses sendBeacon when requested (reliable at page unload). */
const send = (payload, useBeacon = false) => {
  try {
    const body = JSON.stringify(payload);
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    // keepalive lets the request survive a navigation/unload.
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // swallow — never break the guest page
  }
};

/**
 * Track a single event (sent immediately as a 1-item batch).
 * @param {string} reservationId
 * @param {string} surface 'checkin' | 'guestapp'
 * @param {string} type
 * @param {object} metadata
 */
export const trackGuestEvent = (reservationId, surface, type, metadata = {}, useBeacon = false) => {
  if (!reservationId || !surface || !type) return;
  send({
    reservationId,
    sessionId: getSessionId(reservationId),
    surface,
    events: [{ type, metadata }],
  }, useBeacon);
};

/** Convenience for error events. */
export const trackGuestError = (reservationId, surface, kind, message, extra = {}) => {
  if (!reservationId || !surface) return;
  trackGuestEvent(reservationId, surface, 'error', { kind, message, ...extra });
};

/**
 * Start a guest session and measure ACTIVE time accurately.
 *
 * Why segments: beforeunload is unreliable on mobile (the browser often kills a
 * backgrounded tab without firing it), so we must flush on visibilitychange→hidden.
 * But a guest who backgrounds the tab then returns keeps engaging — so instead of
 * one frozen duration, we accumulate visible-time SEGMENTS:
 *   - hidden / pagehide → flush the segment since last resume (a session_end)
 *   - visible           → start a new segment
 * The backend sums all session_end.durationMs → total active time.
 *
 * Idempotent within a page load (StrictMode safe).
 */
export const initGuestSession = (reservationId, surface) => {
  if (!reservationId || !surface) return;
  currentContext = { reservationId, surface };
  if (initialized.has(reservationId)) return;
  initialized.add(reservationId);

  let lastResume = Date.now();

  // Flush the active segment since the last resume. Resets the clock so the
  // next flush (e.g. pagehide right after hidden) measures ~0 and is skipped.
  const flushSegment = (useBeacon) => {
    const now = Date.now();
    const segmentMs = now - lastResume;
    lastResume = now;
    if (segmentMs > 1000) { // ignore <1s noise / double-flush
      trackGuestEvent(reservationId, surface, 'session_end', { durationMs: segmentMs }, useBeacon);
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      flushSegment(true);     // tab backgrounded → bank the active time
    } else {
      lastResume = Date.now(); // tab back in foreground → start a new segment
    }
  };
  const onUnload = () => flushSegment(true);

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onUnload);
  window.addEventListener('beforeunload', onUnload);

  listeners[reservationId] = () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onUnload);
    window.removeEventListener('beforeunload', onUnload);
  };
};

/** Optional manual cleanup (e.g. on SPA route change away from guest page). */
export const endGuestSession = (reservationId) => {
  if (listeners[reservationId]) {
    listeners[reservationId]();
    delete listeners[reservationId];
  }
  initialized.delete(reservationId);
  if (currentContext.reservationId === reservationId) {
    currentContext = { reservationId: null, surface: null };
  }
};
